// Pure Time & Materials (T&M) helpers for the jobq-ui screens (slice 1d). No React, no fetch.
//
// The API is the source of truth (jobq-api tm_pricing.py / main.py "T&M Phase 1c/2c"): this file
// only mirrors the rules the UI must know BEFORE it asks (what is pending, what a visible reason
// is, how a typed price becomes an exact 2-dp string) and builds request bodies. Money and hours
// always go to the API as STRINGS: the API refuses float residue and more than 2 decimals.
//
// Fixed-price records never reach a T&M branch: every predicate here is false for a record whose
// pricing_mode is missing / null / 'fixed' (null pricing_mode = fixed).

// ─── Record predicates ──────────────────────────────────────────────────────────────────────────

/** A quote or sales order header (pricing_mode) that is Time & Materials. */
export function isTm(rec) {
  return !!rec && rec.pricing_mode === 'tm';
}

/** An invoice (GET /invoices/{id}.so_pricing_mode) or a job (so_pricing_mode) on a T&M order. */
export function isTmLinked(rec) {
  return !!rec && rec.so_pricing_mode === 'tm';
}

/** T&M order whose pricing is still to be approved. A NULL status on a T&M row reads as pending. */
export function isTmPending(so) {
  return isTm(so) && so.tm_pricing_status !== 'final';
}

/** T&M order whose pricing has been approved (Approve T&M Pricing ran). */
export function isTmFinal(so) {
  return isTm(so) && so.tm_pricing_status === 'final';
}

const DEAD_INVOICE = new Set(['void', 'voided', 'cancelled']);

/** Non-void invoices of an order's related documents (the same filter handleCreateInvoice uses). */
export function activeInvoices(related) {
  const list = related && Array.isArray(related.invoices) ? related.invoices : [];
  return list.filter((inv) => !DEAD_INVOICE.has(String((inv && inv.status) || '').toLowerCase()));
}

// ─── Reasons (tm_pricing.visible_len) ───────────────────────────────────────────────────────────

export const TM_REASON_MIN = 5;

// Blank-rendering characters tm_pricing._INVISIBLE_CHARS lists by code point.
const INVISIBLE = new Set([
  0x034F, 0x115F, 0x1160, 0x17B4, 0x17B5, 0x180B, 0x180C, 0x180D, 0x180F, 0x2800, 0x3164, 0xFFA0,
]);
const isInvisibleCp = (cp) => INVISIBLE.has(cp) || (cp >= 0xFE00 && cp <= 0xFE0F) || (cp >= 0xE0100 && cp <= 0xE01EF);
const FORMAT_RE = /^\p{Cf}$/u;
const CONTROL_RE = /^\p{Cc}$/u;
// Python's str.isspace() set (the characters tm_pricing strips / keeps): ASCII whitespace, the
// \x1c-\x1f separators, NEL, and the Unicode space separators / line + paragraph separators.
const PY_SPACE = new Set([
  0x09, 0x0A, 0x0B, 0x0C, 0x0D, 0x1C, 0x1D, 0x1E, 0x1F, 0x20, 0x85, 0xA0, 0x1680,
  0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200A,
  0x2028, 0x2029, 0x202F, 0x205F, 0x3000,
]);
const isPySpace = (ch) => PY_SPACE.has(ch.codePointAt(0));

/**
 * How many characters of s a reader SEES (mirror of tm_pricing.visible_len): drop the invisible
 * ones (the listed fillers, Unicode Cf, control characters that are not whitespace), then strip
 * whitespace at both ends. Every T&M reason is judged by it: five zero-width spaces are no reason.
 */
export function visibleLen(s) {
  if (typeof s !== 'string') return 0;
  const kept = [];
  for (const ch of s) {
    const cp = ch.codePointAt(0);
    if (isInvisibleCp(cp)) continue;
    if (FORMAT_RE.test(ch)) continue;
    if (CONTROL_RE.test(ch) && !isPySpace(ch)) continue;
    kept.push(ch);
  }
  let a = 0;
  let b = kept.length;
  while (a < b && isPySpace(kept[a])) a += 1;
  while (b > a && isPySpace(kept[b - 1])) b -= 1;
  return b - a;
}

/** A reason the API will accept (>= TM_REASON_MIN visible characters). */
export function reasonOk(s) {
  return visibleLen(s) >= TM_REASON_MIN;
}

// ─── Money / hours as exact strings ─────────────────────────────────────────────────────────────

const MONEY_TEXT_RE = /^([+-]?)(\d*)(?:\.(\d*))?$/;
const THOUSANDS_RE = /^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d*)?$/;

/**
 * Integer cents of a money value, or null when it is not a number with at most 2 decimals.
 * Strings are read digit by digit (never through a float); numbers must already be on the cent.
 */
export function toCents(v) {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return null;
    const c = Math.round(v * 100);
    return Math.abs(v * 100 - c) < 1e-6 ? c : null;
  }
  let s = String(v).trim().replace(/^\$/, '');
  if (s.includes(',')) {
    // A comma is accepted only as a thousands separator ("1,250.00"). A decimal comma ("12,50")
    // or any other comma is refused: stripping it would read 12,50 as 1250.
    if (!THOUSANDS_RE.test(s)) return null;
    s = s.replace(/,/g, '');
  }
  const m = MONEY_TEXT_RE.exec(s);
  if (!m) return null;
  const [, sign, whole, frac = ''] = m;
  if (!whole && !frac) return null;
  if (frac.length > 2) {
    // Trailing zeros past the cent are fine ("12.500"); anything else is more than 2 decimals.
    if (/[^0]/.test(frac.slice(2))) return null;
  }
  const cents = parseInt(whole || '0', 10) * 100 + parseInt((frac + '00').slice(0, 2), 10);
  if (!Number.isSafeInteger(cents)) return null;
  return sign === '-' ? -cents : cents;
}

/** Cents → '12.30' / '-0.05'. */
export function centsToString(c) {
  if (c === null || c === undefined || !Number.isFinite(c)) return null;
  const neg = c < 0;
  const a = Math.abs(Math.round(c));
  const s = `${Math.floor(a / 100)}.${String(a % 100).padStart(2, '0')}`;
  return neg && a !== 0 ? `-${s}` : s;
}

/** A money value as the exact 2-dp string the API wants ('12.30'), or null when invalid. */
export function money2(v) {
  return centsToString(toCents(v));
}

/** Hours as a string with at most 4 decimals (tm_pricing rounds extras' hours to 0.0001), or null. */
export function hours4(v) {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  const s = typeof v === 'number' ? (Number.isFinite(v) ? String(v) : '') : String(v).trim();
  const m = /^(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (!m[1] && !m[2])) return null;
  const frac = m[2] || '';
  if (frac.length > 4 && /[^0]/.test(frac.slice(4))) return null;
  const whole = String(parseInt(m[1] || '0', 10));
  const f = frac.slice(0, 4).replace(/0+$/, '');
  return f ? `${whole}.${f}` : whole;
}

/** Unit price from a typed LINE total: round-half-up(total ÷ qty) to the cent, plus the residual. */
export function unitFromLineTotal(totalCents, qty) {
  const q = Number(qty);
  if (!Number.isInteger(q) || q <= 0 || totalCents === null || totalCents === undefined) return null;
  const neg = totalCents < 0;
  const a = Math.abs(totalCents);
  let unit = Math.floor(a / q);
  if ((a % q) * 2 >= q) unit += 1;           // half up, in integers
  const unitCents = neg ? -unit : unit;
  return { unitCents, residualCents: totalCents - unitCents * q };
}

// ─── Constants mirrored from tm_pricing.py (keep in step) ───────────────────────────────────────

/** Defect causes (Mike's decision 15; tm_pricing / defects.cause_code CHECK). */
export const CAUSE_OPTIONS = Object.freeze([
  { code: 'process', label: 'Process / setup' },
  { code: 'handling', label: 'Handling damage' },
  { code: 'program_nest', label: 'Program / nest' },
  { code: 'machine', label: 'Machine' },
  { code: 'material_vendor', label: 'Material / vendor' },
  { code: 'customer', label: 'Customer (drawing / change)' },
  { code: 'other', label: 'Other' },
]);

/** Causes that pre-suggest "shop error — don't charge" at pricing (never decide it). */
export const SHOP_ERROR_SUGGEST = Object.freeze(['handling', 'program_nest', 'machine']);

/** tm_pricing.EXTRA_KINDS. */
export const EXTRA_KINDS = Object.freeze([
  'engineering', 'programming', 'setup', 'extra_hours', 'material_adjustment', 'value_transfer', 'minimum_charge',
]);

// Copy of tm_pricing.EXTRA_HOURS_STATION_RATES (station -> the quote rate field it bills at).
export const EXTRA_HOURS_STATIONS = Object.freeze({
  Laser: 'laser_rate', Tap: 'tap_rate', Brake: 'brake_rate', Blast: 'blast_rate', Finish: 'finish_rate',
  Fabrication: 'fabrication_rate', Cutting: 'fabrication_rate', Drilling: 'fabrication_rate',
  Milling: 'fabrication_rate', Tapping: 'fabrication_rate', Lathe: 'fabrication_rate',
  Welding: 'fabrication_rate', Assembly: 'fabrication_rate',
});

/** The 1c acknowledgement codes: a MANUAL draft may acknowledge only these (PHASE1_READINESS_CODES). */
export const PHASE1_ACK_CODES = Object.freeze([
  'zero_billable_line', 'unlinked_invoice_same_parts', 'lineage_invoice', 'sibling_so_same_parts', 'over_nte',
]);

const SEVERITIES = new Set(['hard', 'ack', 'info', 'warning']);

// Copy of tm_pricing.READINESS_SEVERITY after P2-c: 1c's codes, the actuals collector's codes
// (COLLECTOR_READINESS_SEVERITY) and the proposal warnings (PROPOSAL_WARNING_SEVERITY). The server
// already sends each item at its canonical severity; this map is the fallback when an item carries
// none (or a bogus one), and anything in neither is HARD (never a silent pass).
export const TM_READINESS_SEVERITY = Object.freeze({
  // 1c
  not_pending: 'hard', so_closed: 'hard', no_customer: 'hard', no_quote: 'hard', quote_link_broken: 'hard',
  policy_not_bill_shipped: 'hard', no_jobs: 'hard', jobs_not_terminal: 'hard', open_punch: 'hard',
  invoice_exists: 'hard', allocations_touching_so: 'hard', cross_so_placements: 'hard', unsupported_lines: 'hard',
  unlisted_job_parts: 'hard', foreign_customer_parts: 'hard', preview_failed: 'hard', preview_unmatched_lines: 'hard',
  shipped_exceeds_good: 'hard', nothing_billable: 'hard', good_not_shipped: 'hard',
  zero_billable_line: 'ack', unlinked_invoice_same_parts: 'ack', lineage_invoice: 'ack', sibling_so_same_parts: 'ack',
  over_nte: 'ack',
  over_estimate: 'info', preview_drift: 'info', auto_completed_estimate_tasks: 'info', typed_minutes_tasks: 'info',
  zero_time_tasks: 'info',
  // the actuals collector (P2-a/b/c)
  collector_failed: 'hard', staged_laser_defects: 'hard', laser_task_unallocated: 'hard', opo_unallocatable: 'hard',
  override_uncomputable: 'hard',
  auto_estimate_tasks: 'ack', unmeasured_share: 'ack', typed_equals_estimate: 'ack', typed_burst: 'ack',
  zero_time_task: 'ack', long_segment: 'ack', self_overlap: 'ack', milestone_series: 'ack', laser_over_estimate: 'ack',
  material_unknown: 'ack', house_material_unpriced: 'ack', other_owner_material: 'ack', formula_material: 'ack',
  opo_missing_bill: 'ack', station_rate_zero: 'ack', rate_out_of_band: 'ack', decision_on_deleted_defect: 'ack',
  decision_snapshot_mismatch: 'ack', override_link_unconfirmed: 'ack', override_clamped_zero: 'ack',
  decision_unpriced: 'ack', station_pieces_short: 'ack', station_pieces_over: 'ack', non_billable_job_cost: 'ack',
  voided_work_with_time: 'ack', reconciliation_residual: 'ack', negative_time_task: 'ack',
  premium_incomplete: 'warning', overrun: 'warning', laser_weight_fallback: 'warning', deleted_sheet_split: 'warning',
  cutlist_catalog_material: 'warning', gas_wear_estimated: 'warning', mtn_no_cost_fallback: 'warning',
  sheet_defect_priced: 'warning', vendor_scrap: 'warning', sheet_count_zero: 'warning', scrap_premium: 'warning',
  unlinked_remake_candidate: 'warning', remake_material_owner_switch: 'warning',
  // the actuals proposal's own warnings
  zero_bill_line_value: 'warning', billed_under_minimum: 'warning',
});

const CODE_LABELS = {
  not_pending: 'Pricing is not pending',
  so_closed: 'The order is closed or archived',
  no_customer: 'The order has no customer',
  no_quote: 'The order has no quote',
  quote_link_broken: "The quote's lines no longer match the order",
  policy_not_bill_shipped: 'Billing policy is not Ship Any Qty (Bill Shipped)',
  no_jobs: 'No jobs on this order',
  jobs_not_terminal: 'Jobs are still open',
  open_punch: 'Someone is still punched in',
  invoice_exists: 'An invoice already exists',
  allocations_touching_so: 'Surplus allocations touch this order',
  cross_so_placements: "Pieces sit on another order's pallets",
  unsupported_lines: 'Lines T&M cannot price',
  unlisted_job_parts: "Jobs made parts that aren't on the order",
  foreign_customer_parts: "Another customer's parts",
  preview_failed: 'The invoice preview failed',
  preview_unmatched_lines: "The invoice preview doesn't match the lines",
  shipped_exceeds_good: 'More shipped than good pieces',
  nothing_billable: 'Nothing to bill',
  good_not_shipped: 'Good pieces not shipped',
  zero_billable_line: 'A line bills 0 pieces',
  unlinked_invoice_same_parts: 'Another invoice bills these parts',
  lineage_invoice: 'A copy of this quote was invoiced',
  sibling_so_same_parts: 'Another open order has the same parts',
  over_nte: 'Over the not-to-exceed',
  over_estimate: 'Over 125% of the estimate',
  preview_drift: 'The invoice preview moved',
  auto_completed_estimate_tasks: 'Tasks auto-completed at their estimate',
  typed_minutes_tasks: 'Tasks with typed minutes',
  zero_time_tasks: 'Tasks with no time',
  collector_failed: "The cost data couldn't be read",
  staged_laser_defects: 'Laser defects are staged, not recorded',
  laser_task_unallocated: "A laser task's time has no part list",
  opo_unallocatable: "Outside processing can't be put on a line",
  override_uncomputable: "A shop-error amount can't be computed",
  auto_estimate_tasks: 'Tasks auto-completed at the estimate',
  unmeasured_share: 'Much of the labour is typed or estimated',
  typed_equals_estimate: 'Typed minutes equal the estimate',
  typed_burst: 'Several times typed at once',
  zero_time_task: 'A task was completed with no time',
  long_segment: 'A long punch',
  self_overlap: 'One person punched into two tasks at once',
  milestone_series: 'Milestone punches counted once',
  laser_over_estimate: 'A laser task ran over twice its estimate',
  material_unknown: "Plate whose owner is unknown",
  house_material_unpriced: 'House plate with no cost',
  other_owner_material: "Another customer's material",
  formula_material: 'Material from the quote formula',
  opo_missing_bill: 'Outside processing with no vendor bill',
  station_rate_zero: "A station with hours has a $0 rate",
  rate_out_of_band: 'A rate or markup is out of the usual range',
  decision_on_deleted_defect: 'A decision or link refers to a deleted defect',
  decision_snapshot_mismatch: 'A defect changed after its shop-error decision',
  override_link_unconfirmed: 'A possible remake job is not linked',
  override_clamped_zero: 'A shop-error decision removes $0.00',
  decision_unpriced: 'A shop-error decision no line prices',
  station_pieces_short: 'A station counts fewer pieces than expected',
  station_pieces_over: 'A station counts more pieces than expected',
  non_billable_job_cost: 'Cost on a non-billable job',
  voided_work_with_time: 'Voided work with time (not in the price)',
  reconciliation_residual: "Time or material that landed on no line",
  negative_time_task: 'A task with negative time',
  premium_incomplete: 'The scrap premium may read low',
  overrun: 'More pieces than ordered',
  laser_weight_fallback: 'Laser time split by a fallback weight',
  deleted_sheet_split: 'A deleted cut sheet was split by parts',
  cutlist_catalog_material: 'Bar material at the catalog price',
  gas_wear_estimated: 'Gas and wear estimated',
  mtn_no_cost_fallback: 'A material lot with no cost (fallback used)',
  sheet_defect_priced: 'A ruined sheet is in the price',
  vendor_scrap: 'Scrap at an outside vendor',
  sheet_count_zero: 'A sheet task with no sheet count',
  scrap_premium: 'Scrap raises this price a lot',
  unlinked_remake_candidate: 'A possible remake job is not linked',
  remake_material_owner_switch: "A remake used KanCraft's plate",
  zero_bill_line_value: 'A line that bills 0 pieces keeps value that is not billed',
  billed_under_minimum: 'The bill is under the order minimum',
};

/** Human label for a readiness / warning code; unknown codes read as their words. */
export function chipLabel(code) {
  if (!code) return '';
  return CODE_LABELS[code] || String(code).replace(/_/g, ' ');
}

/**
 * The severity the UI shows: the item's own when it is a known severity (the server sends each item
 * at its code's canonical gate), else the code's gate in TM_READINESS_SEVERITY, else HARD (never a
 * silent pass).
 */
export function severityOf(item) {
  const s = item && item.severity;
  if (SEVERITIES.has(s)) return s;
  const byCode = item && TM_READINESS_SEVERITY[item.code];
  return byCode || 'hard';
}

/**
 * Readiness items by severity. acks = {code: reason}; an 'ack' item carries `acked` = its reason
 * is visible and long enough. Unknown severity = hard.
 */
export function groupReadiness(items, acks = {}) {
  const out = { hard: [], ack: [], info: [], warning: [] };
  (Array.isArray(items) ? items : []).forEach((it) => {
    if (!it || typeof it !== 'object') return;
    const sev = severityOf(it);
    const row = { ...it, severity: sev, label: chipLabel(it.code) };
    if (sev === 'ack') row.acked = reasonOk(acks && acks[it.code]);
    out[sev].push(row);
  });
  out.blocking = out.hard.length > 0 || out.ack.some((a) => !a.acked);
  return out;
}

// ─── Request bodies ─────────────────────────────────────────────────────────────────────────────

const trimOrNull = (s) => (typeof s === 'string' && s.trim() ? s.trim() : null);

/**
 * POST tm_runs body for the MANUAL basis (1c). lines: [{so_line_id, unit_price, no_charge_reason}]
 * with unit_price already typed; header: {shipping_amount, additional_fees, discount_amount};
 * acks: {code: reason} (only the 1c codes are sent). Returns {body, errors}; errors name the
 * lines / fields the API would refuse, so the Preview button can say so first.
 */
export function buildManualRunBody({ lines = [], header = {}, acks = {}, note = null, ackCodes = PHASE1_ACK_CODES } = {}) {
  const errors = [];
  const bodyLines = lines.map((l) => {
    const unit = money2(l.unit_price);
    if (unit === null) errors.push(`${l.label || `line ${l.so_line_id}`}: type a unit price (dollars and cents)`);
    else if (toCents(unit) < 0) errors.push(`${l.label || `line ${l.so_line_id}`}: the unit price cannot be negative`);
    const zero = unit !== null && toCents(unit) === 0;
    const reason = zero ? trimOrNull(l.no_charge_reason) : null;
    if (zero && !reasonOk(l.no_charge_reason)) {
      errors.push(`${l.label || `line ${l.so_line_id}`} is $0.00: give a no-charge reason (at least ${TM_REASON_MIN} characters)`);
    }
    return { so_line_id: l.so_line_id, unit_price: unit, no_charge_reason: reason };
  });
  const body = { lines: bodyLines };
  [['shipping_amount', 'Shipping'], ['additional_fees', 'Fees'], ['discount_amount', 'Discount']].forEach(([k, label]) => {
    const raw = header[k];
    const v = raw === '' || raw === null || raw === undefined ? '0.00' : money2(raw);
    if (v === null || toCents(v) < 0) errors.push(`${label}: type an amount of 0.00 or more`);
    body[k] = v;
  });
  body.acknowledgements = Object.keys(acks || {})
    .filter((code) => ackCodes.includes(code) && reasonOk(acks[code]))
    .sort()
    .map((code) => ({ code, reason: acks[code].trim() }));
  body.note = trimOrNull(note);
  return { body, errors };
}

/**
 * POST tm_runs body for the ACTUALS basis (P2-c). lines: [{so_line_id, label, unit_price?,
 * override_reason?, no_charge_reason?, zero_bill?, supported?, suggested_unit_price?}]. A line with no
 * typed unit takes the builder's suggestion (unit_price omitted: price_source 'tm_actuals'); a typed
 * unit is the line's whole price ('tm_manual', it replaces the line's share of the extras) and needs an
 * override reason — unless it is exactly the suggestion, which the server records as 'tm_actuals' and
 * whose reason it drops. "The suggestion" is the server's q2((V + the line's extras) / bill_qty), so
 * the typed unit is compared with typedUnitSuggestion's answer (the collector's suggestion for a line
 * no extra reaches; the current Preview's for one an extra reaches; unknown — a reason is required —
 * without one). A line that bills 0 pieces is always $0.00 and needs a no-charge reason (1c's rule);
 * so does a typed $0.00, and so does a billing line whose suggestion is $0.00 when nothing is typed
 * and no extra could lift it. A line T&M v1 cannot price (supported false: an assembly or purchased-
 * component line) refuses the Preview: the order cannot be approved on either basis until the line is
 * removed or changed (hard readiness item unsupported_lines).
 * extras: plain objects {kind, so_line_id?, from_so_line_id?, station?, hours?, rate?, amount?, reason}
 * sent as strings, each with a reason. carve_outs are never sent (decision 7). ackCodes: the codes an
 * acknowledgement may be sent for (default: every code with a reason). previewed: the last actuals
 * Preview, {key: actualsSuggestionKey(its body), suggested: {so_line_id: its suggested_unit_price}}.
 * Returns {body, errors, suggestions}; suggestions[so_line_id] = typedUnitSuggestion's answer for the
 * line (what the line table shows and compares with).
 */
export function buildActualsRunBody({ actualsHash, lines = [], header = {}, acks = {}, extras = [], note = null, ackCodes = null, previewed = null } = {}) {
  const codes = ackCodes || Object.keys(acks || {});
  const manual = buildManualRunBody({ lines: [], header, acks, note, ackCodes: codes });
  const errors = [...manual.errors];
  if (!actualsHash) errors.push('The actuals are not loaded yet.');
  const typed = (val) => val !== undefined && val !== null && String(val).trim() !== '';
  const extrasIn = Array.isArray(extras) ? extras : [];
  const anyExtras = extrasIn.length > 0;
  // The extras first (their errors still come after the lines'): the lines' suggestions depend on them.
  const xErrors = [];
  let minimums = 0;
  const bodyExtras = extrasIn.map((e) => {
    const x = { kind: e.kind };
    const what = e.kind ? String(e.kind).replace(/_/g, ' ') : 'extra';
    const before = xErrors.length;
    ['so_line_id', 'from_so_line_id', 'station'].forEach((k) => { if (e[k] !== undefined && e[k] !== null && e[k] !== '') x[k] = e[k]; });
    if (typed(e.hours)) {
      x.hours = hours4(e.hours);
      if (x.hours === null) xErrors.push(`${what}: type the hours (up to 4 decimals)`);
      else if (Number(x.hours) <= 0) xErrors.push(`${what}: the hours must be above 0`);
    }
    ['rate', 'amount'].forEach((k) => {
      if (!typed(e[k])) return;
      x[k] = money2(e[k]);
      if (x[k] === null) xErrors.push(`${what}: type the ${k} in dollars and cents`);
    });
    x.reason = trimOrNull(e.reason);
    if (xErrors.length === before) {
      const amt = x.amount != null ? toCents(x.amount) : null;
      if (!EXTRA_KINDS.includes(e.kind)) xErrors.push(`${what}: not a kind of extra the API knows`);
      else if (['engineering', 'programming', 'setup'].includes(e.kind)) {
        if (x.hours == null && amt == null) xErrors.push(`${what}: type the hours (× a rate) or an amount`);
        else if (x.hours == null && amt <= 0) xErrors.push(`${what}: the amount must be above 0.00`);
        else if (x.rate != null && toCents(x.rate) <= 0) xErrors.push(`${what}: the rate must be above 0.00`);
      } else if (e.kind === 'extra_hours') {
        if (!x.station) xErrors.push(`${what}: pick the station`);
        else if (x.hours == null) xErrors.push(`${what}: type the hours`);
      } else if (e.kind === 'material_adjustment') {
        if (amt == null || amt === 0) xErrors.push(`${what}: type an amount (negative for a credit), not 0.00`);
      } else if (e.kind === 'value_transfer') {
        if (x.from_so_line_id == null) xErrors.push(`${what}: pick the line whose value moves`);
        else if (amt == null || amt <= 0) xErrors.push(`${what}: type an amount above 0.00`);
      } else if (e.kind === 'minimum_charge') {
        minimums += 1;
        if (amt == null || amt <= 0) xErrors.push('Order minimum: type an amount above 0.00');
        if (x.so_line_id != null) xErrors.push('Order minimum: it is per order (no line)');
        if (minimums > 1) xErrors.push('Order minimum: only one per order');
      }
      if (!reasonOk(e.reason)) {
        xErrors.push(`${e.kind === 'minimum_charge' ? 'Order minimum' : what}: give a reason (at least ${TM_REASON_MIN} characters)`);
      }
    }
    return x;
  });
  const previewCurrent = !!previewed && previewed.key === actualsSuggestionKey({ expected_actuals_hash: actualsHash || null, extras: bodyExtras });
  const suggestions = {};
  const lErrors = [];
  const bodyLines = lines.map((l) => {
    const name = l.label || `line ${l.so_line_id}`;
    const out = { so_line_id: l.so_line_id };
    if (l.supported === false) {
      // The builder values it 0.00 and the order is hard-blocked (unsupported_lines, on either basis): say so first.
      lErrors.push(`${name} cannot be priced by T&M v1 (assemblies / purchased components): the order cannot be approved until the line is removed or changed`);
      return out;
    }
    let zero = false;
    const suggestedCents = l.suggested_unit_price == null ? null : toCents(l.suggested_unit_price);
    const sug = typedUnitSuggestion({ line: l, extras: extrasIn, previewed: previewCurrent ? previewed : null });
    suggestions[l.so_line_id] = sug;
    if (l.zero_bill) {
      zero = true;
    } else if (typed(l.unit_price)) {
      const unit = money2(l.unit_price);
      if (unit === null) lErrors.push(`${name}: type a unit price (dollars and cents)`);
      else if (toCents(unit) < 0) lErrors.push(`${name}: the unit price cannot be negative`);
      out.unit_price = unit;
      out.override_reason = trimOrNull(l.override_reason);
      if (!isTypedSuggestion(unit, sug) && !reasonOk(l.override_reason)) {
        lErrors.push(sug.from === null
          ? `${name}: a price of your own needs a reason (at least ${TM_REASON_MIN} characters); the extras change this line's suggestion: leave the unit blank and Preview to see it`
          : `${name}: a price of your own needs a reason (at least ${TM_REASON_MIN} characters)`);
      }
      zero = unit !== null && toCents(unit) === 0;
    } else if (suggestedCents === 0 && !anyExtras) {
      zero = true;
    }
    if (zero && !reasonOk(l.no_charge_reason)) {
      lErrors.push(l.zero_bill
        ? `${name} bills 0 pieces: give a no-charge reason (at least ${TM_REASON_MIN} characters)`
        : `${name} is $0.00: give a no-charge reason (at least ${TM_REASON_MIN} characters)`);
    }
    if (trimOrNull(l.no_charge_reason)) out.no_charge_reason = trimOrNull(l.no_charge_reason);
    return out;
  });
  errors.push(...lErrors, ...xErrors);
  const body = {
    basis: 'so_line_actuals',
    expected_actuals_hash: actualsHash || null,
    lines: bodyLines,
    extras: bodyExtras,
    shipping_amount: manual.body.shipping_amount,
    additional_fees: manual.body.additional_fees,
    discount_amount: manual.body.discount_amount,
    acknowledgements: manual.body.acknowledgements,
    note: manual.body.note,
  };
  return { body, errors, suggestions };
}

/**
 * Whether an extra can move a line's value (and so its suggestion): one aimed at the line, one that
 * moves value out of it, and every extra with no line (spread over the billing lines) — the order
 * minimum always (its top-up spreads). Conservative: a spread that happens to give the line 0 cents
 * still counts.
 */
export function extrasReachLine(extras, soLineId) {
  const id = String(soLineId);
  return (Array.isArray(extras) ? extras : []).some((e) => {
    if (!e || typeof e !== 'object') return false;
    if (e.kind === 'minimum_charge') return true;
    const on = e.so_line_id;
    if (on === undefined || on === null || on === '') return true;
    return String(on) === id || (e.from_so_line_id != null && String(e.from_so_line_id) === id);
  });
}

/**
 * What a typed unit on an actuals line is compared with to count as "the suggestion" (tm_actuals, no
 * reason). The server compares it with q2((V + the line's extras) / bill_qty) — the suggestion AFTER the
 * extras and the minimum's top-up (tm_pricing.build_actuals_proposal). previewed: the last Preview,
 * {suggested: {so_line_id: '21.29'}}, passed only while it was made on these very actuals and extras.
 *   no extra reaches the line  → the collector's suggested_unit_price       (from 'collector')
 *   an extra reaches it + a current Preview → that Preview's suggestion      (from 'preview')
 *   an extra reaches it, no current Preview → unknown: never guessed         (from null)
 * Returns {suggested: '21.29' | null, from: 'collector' | 'preview' | null}.
 */
export function typedUnitSuggestion({ line, extras = [], previewed = null } = {}) {
  const sid = line && line.so_line_id;
  if (!extrasReachLine(extras, sid)) return { suggested: (line && line.suggested_unit_price) ?? null, from: 'collector' };
  const pv = previewed && previewed.suggested ? previewed.suggested[sid] : undefined;
  if (pv !== undefined && pv !== null && toCents(pv) !== null) return { suggested: String(pv), from: 'preview' };
  return { suggested: null, from: null };
}

/** A typed unit (a 2-dp string or null) is exactly the suggestion typedUnitSuggestion found (never when it is unknown). */
export function isTypedSuggestion(unit, sug) {
  if (unit === null || unit === undefined || !sug || sug.suggested == null) return false;
  const u = toCents(unit);
  return u !== null && u === toCents(sug.suggested);
}

/**
 * The inputs an actuals suggestion depends on — the cost data (its hash) and the extras as sent — as
 * a stable string. A Preview's per-line suggestions hold while this is unchanged, whatever units,
 * reasons, header money or acknowledgements are typed after it.
 */
export function actualsSuggestionKey(body) {
  return canonical({ hash: (body && body.expected_actuals_hash) ?? null, extras: (body && body.extras) || [] });
}

function canonical(v) {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v === undefined ? null : v);
}

/** A stable signature of a tm_runs body: Approve is offered only while the inputs still match the Preview. */
export function previewSignature(body) {
  return canonical(body || null);
}

/**
 * Lines whose actuals moved between two tm_line_actuals payloads (by so_line_id; compares the
 * line value V, the billable quantity and the suggested unit). Used after a 409 actuals_changed to
 * highlight them. A tm_line_actuals line carries value: {labour, material, ..., V} and
 * qty: {..., billable}; a proposal line's flat line_value / bill_qty are read as a fallback.
 */
export function actualsDiff(before, after) {
  const isObj = (x) => !!x && typeof x === 'object';
  const key = (l) => {
    const value = isObj(l.value) ? l.value.V : (l.line_value ?? l.value);
    const qty = isObj(l.qty) ? l.qty.billable : l.bill_qty;
    return [value ?? null, qty ?? null, l.suggested_unit_price ?? null].map(String).join('|');
  };
  const idx = new Map(((before && before.lines) || []).map((l) => [l.so_line_id, key(l)]));
  const moved = [];
  ((after && after.lines) || []).forEach((l) => {
    if (!idx.has(l.so_line_id) || idx.get(l.so_line_id) !== key(l)) moved.push(l.so_line_id);
  });
  return moved;
}

/**
 * Rows for the Approve confirmation: every line of the proposal with what was ordered, what will
 * be billed and at what price (the server's strings, never recomputed).
 */
export function approveRows(proposal, contextLines = [], labelOf = null) {
  const byId = new Map((contextLines || []).map((l) => [l.so_line_id, l]));
  return ((proposal && proposal.lines) || []).map((pl) => {
    const cl = byId.get(pl.so_line_id) || {};
    return {
      so_line_id: pl.so_line_id,
      label: labelOf ? labelOf(pl.so_line_id) : (cl.description || `line ${pl.so_line_id}`),
      ordered: cl.quantity_ordered ?? null,
      bill_qty: pl.bill_qty,
      unit_price: pl.unit_price,
      line_total: pl.line_total,
      price_source: pl.price_source || null,
      no_charge_reason: pl.no_charge_reason || null,
      override_reason: pl.override_reason || null,
      suggested_unit_price: pl.suggested_unit_price ?? null,
    };
  });
}

/**
 * A tm_runs / tm_finalize 400's errors[] split by line. The server names a line by its so_line_id
 * ("line 4711 bills 0 pieces ..." / "line 801: unit_price ..."), which the table never shows: an
 * error for a line on this order goes to that row, with the id replaced by the row's label.
 * Everything else (header money, the total) stays in the general list.
 * Returns {byLine: {so_line_id: [text]}, rest: [text]}.
 */
export function splitLineErrors(errors, lineIds = [], labelOf = null) {
  const known = new Set((lineIds || []).map(String));
  const byLine = {};
  const rest = [];
  (Array.isArray(errors) ? errors : []).forEach((e) => {
    const text = String(e);
    const m = /^line (\d+)(?=[\s:])/.exec(text);
    if (!m || !known.has(m[1])) { rest.push(text); return; }
    const label = labelOf ? labelOf(m[1]) : `line ${m[1]}`;
    (byLine[m[1]] = byLine[m[1]] || []).push(`${label}${text.slice(m[0].length)}`);
  });
  return { byLine, rest };
}

// ─── Booked revenue (Invoices dashboard) ────────────────────────────────────────────────────────

/**
 * Booked Revenue (uninvoiced commitment) over open sales orders, with T&M orders whose pricing is
 * still PENDING left out (their commitment is only shipping + fees until priced). For a list with
 * no pending T&M order the total and count are exactly the old arithmetic.
 */
export function bookedRevenueSplit(orders) {
  const list = Array.isArray(orders) ? orders : [];
  const pending = list.filter(isTmPending);
  const counted = list.filter((so) => !isTmPending(so));
  const withUninvoiced = counted.filter((so) => {
    const commitment = Number(so.commitment) || 0;
    const invoiced = Number(so.total_invoiced_amt) || 0;
    return commitment - invoiced > 0;
  });
  const total = withUninvoiced.reduce((sum, so) =>
    sum + ((Number(so.commitment) || 0) - (Number(so.total_invoiced_amt) || 0)), 0);
  const tmPendingEst = pending.reduce((s, so) => s + (Number(so.tm_estimate_total) || 0), 0);
  return { total, count: withUninvoiced.length, tmPendingCount: pending.length, tmPendingEst };
}

// ─── Sales Order → invoice ──────────────────────────────────────────────────────────────────────

/**
 * POST /invoices body from the Sales Order page. Fixed-price: exactly the body the page has always
 * sent (key order included). T&M: only {customer_id, sales_order_id, notes} — the server takes the
 * job list and the approved header money from the order (the one close-out invoice).
 */
export function soInvoicePayload(header, related, tm) {
  if (tm) {
    return {
      customer_id: header.customer_id,
      sales_order_id: header.sales_order_id,
      notes: `T&M close-out invoice for Sales Order #${header.order_number}`,
    };
  }
  const jobIds = related?.jobs?.map((j) => j.job_id) || [];
  return {
    customer_id: header.customer_id,
    sales_order_id: header.sales_order_id,
    shipping_amount: parseFloat(header.shipping_amount || 0),
    additional_fees: parseFloat(header.additional_fees || 0),
    tax_amount: parseFloat(header.tax_amount || 0),
    discount_amount: parseFloat(header.discount_amount || 0),
    job_ids: jobIds,
    notes: `Generated from Sales Order #${header.order_number}`,
  };
}

/**
 * The Create Invoice button on a T&M order: {disabled, title, invoice}. Pending → disabled until
 * pricing is approved; final with an active invoice → disabled (one close-out invoice). Fixed →
 * enabled with the usual title.
 */
export function tmInvoiceGate(header, related) {
  if (!isTm(header)) return { disabled: false, title: 'Generate Invoice from Order', invoice: null };
  if (isTmPending(header)) return { disabled: true, title: 'Approve T&M pricing first', invoice: null };
  const inv = activeInvoices(related)[0] || null;
  if (inv) {
    return { disabled: true, title: `A T&M order has one close-out invoice (#${inv.invoice_number})`, invoice: inv };
  }
  return { disabled: false, title: 'Create the T&M close-out invoice (the approved total)', invoice: null };
}

// ─── Manual pricing (the "Price by hand" basis) ─────────────────────────────────────────────────

/** Starting input of one line: last approved price (prefill_unit_price) or blank; a 0-bill line is $0. */
export function initialManualInput(ctxLine) {
  const bill = Number(ctxLine && ctxLine.billable) || 0;
  if (bill === 0) return { mode: 'unit', unit: '0.00', total: '', reason: '' };
  const prefill = money2(ctxLine && ctxLine.prefill_unit_price);
  return { mode: 'unit', unit: prefill || '', total: '', reason: '' };
}

/**
 * What one manual line will send and show. input = {mode: 'unit' | 'total', unit, total, reason}.
 * A typed LINE total becomes a cents unit price (half up) and a residual the unit can't carry.
 * A line that bills 0 pieces is always $0.00 and needs a no-charge reason (the API's rule).
 */
export function manualLineView(ctxLine, input = {}) {
  const billQty = Number(ctxLine && ctxLine.billable) || 0;
  const zeroBill = billQty === 0;
  let unitCents = null;
  let residualCents = null;
  let error = null;
  if (zeroBill) {
    unitCents = 0;
  } else if (input.mode === 'total') {
    const tc = toCents(input.total);
    if (String(input.total || '').trim() === '') error = 'Type a line total';
    else if (tc === null || tc < 0) error = 'Line total: dollars and cents, 0 or more';
    else {
      const r = unitFromLineTotal(tc, billQty);
      unitCents = r.unitCents;
      residualCents = r.residualCents;
    }
  } else {
    const uc = toCents(input.unit);
    if (String(input.unit || '').trim() === '') error = 'Type a unit price';
    else if (uc === null || uc < 0) error = 'Unit price: dollars and cents, 0 or more';
    else unitCents = uc;
  }
  const needsReason = unitCents === 0;
  if (!error && needsReason && !reasonOk(input.reason)) {
    error = zeroBill
      ? `Bills 0 pieces: give a no-charge reason (at least ${TM_REASON_MIN} characters)`
      : `$0.00: give a no-charge reason (at least ${TM_REASON_MIN} characters)`;
  }
  return {
    billQty,
    zeroBill,
    unit: unitCents === null ? null : centsToString(unitCents),
    unitCents,
    residualCents,
    extendedCents: unitCents === null ? null : unitCents * billQty,
    needsReason,
    error,
  };
}

// ─── Actuals pricing (the "From actuals" basis, P2-c) ───────────────────────────────────────────

const numOr0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** The value parts of an actuals line, in the order they foot: labour + material + gas/wear + vendor + extras − shop error removed − carve-out + rounding = V. */
export const VALUE_PARTS = Object.freeze([
  ['labour', 'Labour (hours × quote rates)', 1],
  ['material', 'Material (with markup)', 1],
  ['gas_wear', 'Laser gas + wear (at cost)', 1],
  ['vendor', 'Outside processing (with markup)', 1],
  ['extras', 'Extras', 1],
  ['override_removed', 'Shop error removed', -1],
  ['carve_out', 'Carve-out', -1],
  ['rounding', 'Rounding', 1],
]);

/**
 * Does a line's value breakdown foot to its V (to the cent)? parts: [[key, label, sign, cents]];
 * foots false means the numbers on screen do not add up (never expected: said, not hidden).
 */
export function valueBreakdown(value) {
  const v = value && typeof value === 'object' ? value : {};
  const parts = VALUE_PARTS.map(([k, label, sign]) => [k, label, sign, toCents(v[k] ?? '0') ?? 0]);
  const sum = parts.reduce((acc, [, , sign, c]) => acc + sign * c, 0);
  const V = toCents(v.V ?? '0');
  return { parts, V, sum, foots: V !== null && sum === V };
}

/** A tm_line_actuals line as the collapsed row shows it (display only; every money figure is the server's string). */
export function actualsLineView(line) {
  const l = line || {};
  const qty = l.qty || {};
  const value = l.value || {};
  const hours = { measured: 0, typed: 0, estimate: 0, total: 0 };
  (Array.isArray(l.stations) ? l.stations : []).forEach((st) => {
    const c = st.capture || {};
    hours.measured += numOr0(c.measured);
    hours.typed += numOr0(c.typed);
    hours.estimate += numOr0(c.auto_estimate);
    hours.total += numOr0(st.hours);
  });
  const chips = Array.isArray(l.chips) ? l.chips : [];
  const billable = Number(qty.billable) || 0;
  return {
    supported: l.supported !== false,
    billable,
    zeroBill: billable === 0,
    hours,
    labour: value.labour ?? null,
    material: value.material ?? null,
    gasWear: value.gas_wear ?? null,
    vendor: value.vendor ?? null,
    V: value.V ?? null,
    suggested: l.suggested_unit_price ?? null,
    residual: l.residual ?? null,
    deltaEst: (l.estimate_at_acceptance && l.estimate_at_acceptance.delta) ?? null,
    estUnit: (l.estimate_at_acceptance && l.estimate_at_acceptance.unit) ?? null,
    matChips: chips.filter((c) => String(c).startsWith('mat:')).map((c) => String(c).slice(4)),
    otherChips: chips.filter((c) => !String(c).startsWith('mat:')),
    valueLeft: billable === 0 && (toCents(value.V ?? '0') || 0) > 0,
  };
}

/**
 * A decimal string (or number) rounded to the cent HALF UP on its digits, never through a float: the
 * mirror of Decimal(x).quantize(Decimal('0.01'), ROUND_HALF_UP) (ties away from zero). null when it
 * is not a plain decimal.
 */
export function roundHalfUp2(v) {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  const s = typeof v === 'number' ? (Number.isFinite(v) ? String(v) : '') : String(v).trim();
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (!m[2] && !m[3])) return null;
  const [, sign, whole, frac = ''] = m;
  let cents = parseInt(whole || '0', 10) * 100 + parseInt((frac + '00').slice(0, 2), 10);
  if (frac.length > 2 && frac.charCodeAt(2) >= 53) cents += 1;        // the third decimal is 5-9
  if (!Number.isSafeInteger(cents)) return null;
  return centsToString(sign === '-' ? -cents : cents);
}

/**
 * The quote rate as the 2-dp hint a time extra is prefilled with ('' when the quote has none): rounded
 * half up on the decimal like tm_pricing.apply_extras' own hint (quotes.fabrication_rate may carry
 * more than 2 decimals, e.g. 64.085 → 64.09).
 */
export function rateHint(rate) {
  if (rate === null || rate === undefined || rate === '') return '';
  const r = roundHalfUp2(rate);
  if (r !== null) return r;
  const n = Number(rate);
  return Number.isFinite(n) ? centsToString(Math.round(n * 100)) : '';
}

/** The stations an extra_hours entry may name, with the quote rate each bills at; ok false = a null / $0 rate (the API refuses it). */
export function extraHoursStations(rates) {
  const r = rates || {};
  return Object.keys(EXTRA_HOURS_STATIONS).map((station) => {
    const field = EXTRA_HOURS_STATIONS[station];
    const rate = r[field];
    const ok = rate !== null && rate !== undefined && numOr0(rate) > 0;
    return { station, field, rate: rate ?? null, ok };
  });
}

/**
 * The quantity a candidate remake link is confirmed with: the collector's assigned pieces for that
 * job, else min(the defect's unlinked pieces, the job's pieces). 0 = nothing left to link.
 */
export function linkQty(pricing, jobId, jobPieces) {
  const p = pricing || {};
  const assigned = (Array.isArray(p.assigned_candidates) ? p.assigned_candidates : [])
    .find((a) => String(a.job_id) === String(jobId));
  if (assigned && Number(assigned.pieces) > 0) return Math.floor(Number(assigned.pieces));
  const open = Math.max(0, Math.floor(numOr0(p.open_qty)));
  const pcs = Math.max(0, Math.floor(numOr0(jobPieces)));
  return Math.min(open, pcs);
}

/**
 * The actuals after a 409 actuals_changed: the fresh numbers the refusal carried (hash, rates, lines,
 * totals, other_share) over what was loaded; readiness / warnings / reconciliation stay until the
 * background re-read replaces them.
 */
export function applyChangedActuals(prev, fresh, hash) {
  const f = fresh && typeof fresh === 'object' ? fresh : {};
  const next = { ...(prev || {}) };
  ['rates', 'lines', 'totals', 'other_share', 'builder_version'].forEach((k) => { if (f[k] !== undefined) next[k] = f[k]; });
  next.actuals_hash = hash || f.actuals_hash || next.actuals_hash || null;
  next.actuals_available = true;
  return next;
}

let extraSeq = 0;
/** A fresh extra input row for the Extras card. */
export function newExtraInput(kind, patch = {}) {
  extraSeq += 1;
  return {
    key: `x${extraSeq}`, kind, mode: kind === 'material_adjustment' || kind === 'value_transfer' ? 'amount' : 'hours',
    so_line_id: '', from_so_line_id: '', station: '', hours: '', rate: '', amount: '', reason: '', ...patch,
  };
}

/** An Extras card row → the plain extra buildActualsRunBody sends (only the fields its kind and mode use). */
export function extraToRequest(x) {
  const out = { kind: x.kind };
  if (x.kind !== 'minimum_charge' && x.so_line_id !== '' && x.so_line_id != null) out.so_line_id = Number(x.so_line_id);
  if (x.kind === 'value_transfer' && x.from_so_line_id !== '' && x.from_so_line_id != null) out.from_so_line_id = Number(x.from_so_line_id);
  if (x.kind === 'extra_hours') {
    if (x.station) out.station = x.station;
    out.hours = x.hours;
  } else if (['engineering', 'programming', 'setup'].includes(x.kind)) {
    if (x.mode === 'amount') out.amount = x.amount;
    else { out.hours = x.hours; out.rate = x.rate; }
  } else {
    out.amount = x.amount;
  }
  out.reason = x.reason;
  return out;
}

/** A stored actuals proposal's extras back into Extras card rows + the order minimum (Resume draft). */
export function extrasFromProposal(proposal) {
  const rows = [];
  let minimum = { amount: '', reason: '' };
  ((proposal && proposal.extras) || []).forEach((e) => {
    if (e.kind === 'minimum_charge') {
      minimum = { amount: e.amount ?? '', reason: e.reason || '' };
      return;
    }
    const timed = ['engineering', 'programming', 'setup'].includes(e.kind);
    rows.push(newExtraInput(e.kind, {
      mode: timed ? (e.hours != null ? 'hours' : 'amount') : (e.kind === 'extra_hours' ? 'hours' : 'amount'),
      so_line_id: e.so_line_id ?? '', from_so_line_id: e.from_so_line_id ?? '', station: e.station || '',
      hours: e.hours ?? '', rate: timed && e.hours != null ? (e.rate ?? '') : '',
      amount: e.kind === 'extra_hours' || (timed && e.hours != null) ? '' : (e.amount ?? ''),
      reason: e.reason || '',
    }));
  });
  return { rows, minimum };
}
